import type { Locale } from "@/i18n/translations";

export type GuideSection = {
  heading: string;
  paragraphs?: string[];
  list?: string[];
};

export type GuideBody = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  readingTime: string;
  intro: string;
  sections: GuideSection[];
  closing: string;
};

export type Guide = {
  id: string;
  updated: string;
  content: Record<Locale, GuideBody>;
};

export const GUIDES: Guide[] = [
  {
    id: "how-to-plan-a-wedding",
    updated: "2026-09-09",
    content: {
      en: {
        slug: "how-to-plan-a-wedding",
        title: "How to plan a wedding: a month-by-month timeline",
        metaTitle: "How to plan a wedding — a month-by-month timeline | Nellove",
        description:
          "A practical month-by-month wedding planning timeline: what to decide first, when to book providers, when invitations go out, and what the final week actually looks like.",
        readingTime: "8 min read",
        intro:
          "Most wedding planning stress comes from doing things in the wrong order — choosing flowers before knowing the guest count, or picking a venue before agreeing on a budget. This is the order that works, from the first decision to the week of the wedding.",
        sections: [
          {
            heading: "Start with three numbers, not with inspiration",
            paragraphs: [
              "Before you look at a single venue, settle three things between you: the total budget, roughly how many guests, and the season or date. Everything else is a consequence of those three.",
              "Guest count is the multiplier. Catering, stationery, rentals and often the venue itself are priced per head, so a guest list that grows by twenty people rarely costs a little more — it costs proportionally more across half your budget.",
            ],
          },
          {
            heading: "12 months out: the decisions everything else depends on",
            list: [
              "Agree the total budget and who is contributing.",
              "Estimate the guest count within about ten people.",
              "Choose a date, or two or three dates you could live with.",
              "Shortlist and visit venues, then book one and lock the date.",
            ],
            paragraphs: [
              "At a venue visit, ask what the price actually includes, what time you have to be out, whether you must use their catering, and what happens if you cancel. Those four answers separate venues far better than the photos do.",
            ],
          },
          {
            heading: "9 months out: book the people who only take one wedding a day",
            paragraphs: [
              "Photographers, caterers, musicians and officiants can only be in one place. They are booked earliest and they are the ones you cannot replace at short notice. Confirm them before anything decorative.",
              "This is also when the real guest list gets built — names, emails, postal addresses — and when save-the-dates go out to anyone who has to travel or take time off.",
            ],
          },
          {
            heading: "6 to 4 months out: outfits, paperwork, invitations",
            list: [
              "Order outfits, and get the delivery date in writing — alterations take weeks.",
              "Book florist, cake, hair and makeup, and schedule a makeup trial.",
              "Check the legal requirements where you are marrying; rules and waiting periods differ by country and province, so use the official source for your location.",
              "Order invitations and decide, in one sentence, how guests will RSVP and by when.",
            ],
          },
          {
            heading: "2 months out: write the day down",
            paragraphs: [
              "Invitations go out, and the plan stops being a set of bookings and becomes a timeline. Start from the ceremony time and work outwards in both directions: hair and makeup backwards, photos, arrivals, meal, speeches and music forwards.",
              "A written run-of-show is the single most useful document of the whole process, because it is what you hand to providers and to the people helping you.",
            ],
          },
          {
            heading: "1 month out: close the list",
            list: [
              "Chase the missing RSVPs — there will be missing RSVPs.",
              "Give the caterer the final head count.",
              "Decide seating, if you are seating guests.",
              "Schedule the remaining balances so no payment lands on the day.",
              "Share the timeline with providers and with everyone standing beside you.",
            ],
          },
          {
            heading: "The week of: confirm, delegate, stop",
            paragraphs: [
              "Nothing new should be added in the final week. Confirm arrival times with each provider, check the forecast and the wet-weather plan, pack outfits and documents, and hand one trusted person the timeline and the phone numbers so nobody asks you a logistics question on the day.",
            ],
          },
        ],
        closing:
          "The timeline above is generic on purpose — every wedding shifts it. What matters is that each decision happens after the one it depends on, and that the whole plan lives somewhere both of you can see it.",
      },
      fr: {
        slug: "comment-organiser-un-mariage",
        title: "Comment organiser un mariage : le rétroplanning mois par mois",
        metaTitle: "Comment organiser un mariage — rétroplanning mois par mois | Nellove",
        description:
          "Un rétroplanning de mariage concret, mois par mois : quoi décider en premier, quand réserver les prestataires, quand envoyer les invitations et à quoi ressemble vraiment la dernière semaine.",
        readingTime: "8 min de lecture",
        intro:
          "L'essentiel du stress vient de l'ordre des décisions : choisir les fleurs avant de connaître le nombre d'invités, ou réserver un lieu avant de s'être mis d'accord sur le budget. Voici l'ordre qui fonctionne, de la première décision à la semaine du mariage.",
        sections: [
          {
            heading: "Commencez par trois chiffres, pas par l'inspiration",
            paragraphs: [
              "Avant même de regarder un lieu, fixez trois choses à deux : le budget total, le nombre approximatif d'invités, et la saison ou la date. Tout le reste en découle.",
              "Le nombre d'invités est le multiplicateur. Traiteur, papeterie, locations et souvent le lieu lui-même se facturent par personne : vingt invités de plus, ce n'est pas un peu plus cher, c'est proportionnellement plus cher sur la moitié du budget.",
            ],
          },
          {
            heading: "12 mois avant : les décisions dont tout dépend",
            list: [
              "Fixer le budget total et qui contribue.",
              "Estimer le nombre d'invités à une dizaine près.",
              "Choisir une date, ou deux ou trois dates acceptables.",
              "Présélectionner et visiter des lieux, puis réserver et bloquer la date.",
            ],
            paragraphs: [
              "En visite, demandez ce que le prix inclut réellement, à quelle heure il faut libérer les lieux, si le traiteur est imposé, et ce qui se passe en cas d'annulation. Ces quatre réponses distinguent les lieux bien mieux que les photos.",
            ],
          },
          {
            heading: "9 mois avant : réservez ceux qui ne prennent qu'un mariage par jour",
            paragraphs: [
              "Photographes, traiteurs, musiciens et officiants ne peuvent être qu'à un seul endroit. Ce sont eux qu'on réserve le plus tôt et qu'on ne remplace pas à la dernière minute. Confirmez-les avant tout ce qui est décoratif.",
              "C'est aussi le moment de construire la vraie liste d'invités — noms, courriels, adresses — et d'envoyer les save-the-date à ceux qui doivent voyager ou poser des congés.",
            ],
          },
          {
            heading: "6 à 4 mois avant : tenues, démarches, invitations",
            list: [
              "Commander les tenues, avec la date de livraison par écrit : les retouches prennent des semaines.",
              "Réserver fleuriste, gâteau, coiffure et maquillage, et prévoir un essai.",
              "Vérifier les démarches légales du lieu de mariage : les règles et les délais varient selon le pays et la province, consultez la source officielle.",
              "Commander les invitations et décider, en une phrase, comment et avant quand les invités répondent.",
            ],
          },
          {
            heading: "2 mois avant : mettez la journée par écrit",
            paragraphs: [
              "Les invitations partent, et le projet cesse d'être une série de réservations pour devenir un déroulé. Partez de l'heure de la cérémonie et remontez dans les deux sens : coiffure et maquillage en amont, photos, arrivées, repas, discours et musique en aval.",
              "Ce déroulé écrit est le document le plus utile de tout le processus : c'est lui que vous remettrez aux prestataires et à vos proches.",
            ],
          },
          {
            heading: "1 mois avant : clôturez la liste",
            list: [
              "Relancer les RSVP manquants — il y en aura.",
              "Donner le nombre final au traiteur.",
              "Décider du placement, si vous placez vos invités.",
              "Planifier les soldes pour qu'aucun paiement ne tombe le jour même.",
              "Partager le déroulé avec les prestataires et vos proches impliqués.",
            ],
          },
          {
            heading: "La semaine du mariage : confirmer, déléguer, s'arrêter",
            paragraphs: [
              "Rien de nouveau ne devrait s'ajouter la dernière semaine. Confirmez les heures d'arrivée de chaque prestataire, vérifiez la météo et le plan B, préparez tenues et documents, et confiez le déroulé et les numéros à une personne de confiance pour que personne ne vous pose de question logistique le jour J.",
            ],
          },
        ],
        closing:
          "Ce rétroplanning est volontairement générique : chaque mariage le décale. L'important, c'est que chaque décision arrive après celle dont elle dépend, et que le plan soit visible par vous deux au même endroit.",
      },
    },
  },
  {
    id: "guest-list-and-rsvp",
    updated: "2026-09-09",
    content: {
      en: {
        slug: "wedding-guest-list-and-rsvp",
        title: "Wedding guest list and RSVP, without a spreadsheet",
        metaTitle: "Wedding guest list and RSVP without a spreadsheet | Nellove",
        description:
          "How to build a wedding guest list that survives changes, choose one RSVP method, chase replies without nagging, and get to a final head count you can trust.",
        readingTime: "6 min read",
        intro:
          "The guest list is the piece of wedding planning most likely to break. It changes constantly, several people edit it, and the caterer needs one accurate number at the end. Here is how to keep it under control.",
        sections: [
          {
            heading: "Why the spreadsheet fails",
            paragraphs: [
              "A spreadsheet is fine on day one. It breaks when two people edit different copies, when a plus-one becomes two children, when someone replies by text instead of the form, and when nobody can remember whether the third column meant invited or confirmed.",
              "What you actually need is one list where each guest has a status that can only be one thing at a time.",
            ],
          },
          {
            heading: "Give every guest one status",
            list: [
              "Invited — the invitation has been sent.",
              "Attending — they replied yes.",
              "Not attending — they replied no.",
              "Waiting — no reply yet, and you know how long it has been.",
            ],
            paragraphs: [
              "Anything else — meal choice, dietary needs, accommodation, travel — is a detail attached to the guest, not a fifth status. Keeping status simple is what makes the head count trustworthy.",
            ],
          },
          {
            heading: "Decide the household unit early",
            paragraphs: [
              "Decide whether you are inviting people or households, and be consistent. A household of four counted as one line is where head counts go wrong. Children count as guests for catering even when they eat less, so give them their own line with a note.",
              "Write down your plus-one rule in one sentence before invitations go out, and apply it to everyone. The rule being clear matters more than what the rule is.",
            ],
          },
          {
            heading: "Choose one RSVP method and one deadline",
            paragraphs: [
              "Whether it is a unique link per guest, a public form with a passcode, or a reply card, pick one and put only that on the invitation. Two methods means two lists to reconcile.",
              "Set the deadline two to three weeks before the caterer needs the final number, not on the same day. That gap is where the chasing happens.",
            ],
          },
          {
            heading: "Chasing replies without nagging",
            list: [
              "One reminder about two weeks before the deadline, to everyone still waiting.",
              "One personal message after the deadline, only to the people you genuinely need an answer from.",
              "Treat silence after that as a no, and say so in your own notes so the number stops moving.",
            ],
          },
          {
            heading: "The final count",
            paragraphs: [
              "When you give the caterer a number, write down which statuses you counted and on what date. If the number is questioned later, you will know exactly how it was built — and that is the difference between a calm final week and a stressful one.",
            ],
          },
        ],
        closing:
          "A guest list that is boring to maintain is a guest list that works. One place, one status per guest, one RSVP method, one deadline.",
      },
      fr: {
        slug: "liste-invites-et-rsvp",
        title: "Liste d'invités et RSVP, sans tableur",
        metaTitle: "Liste d'invités et RSVP de mariage, sans tableur | Nellove",
        description:
          "Comment construire une liste d'invités qui résiste aux changements, choisir une seule méthode de RSVP, relancer sans harceler, et obtenir un nombre final fiable pour le traiteur.",
        readingTime: "6 min de lecture",
        intro:
          "La liste d'invités est la partie du mariage qui casse le plus souvent. Elle change tout le temps, plusieurs personnes la modifient, et le traiteur attend un chiffre juste à la fin. Voici comment la garder sous contrôle.",
        sections: [
          {
            heading: "Pourquoi le tableur ne tient pas",
            paragraphs: [
              "Un tableur fonctionne le premier jour. Il casse quand deux personnes modifient deux copies, quand un « plus un » devient deux enfants, quand quelqu'un répond par SMS au lieu du formulaire, et quand plus personne ne sait si la troisième colonne voulait dire invité ou confirmé.",
              "Ce qu'il vous faut, c'est une seule liste où chaque invité a un statut qui ne peut être qu'une chose à la fois.",
            ],
          },
          {
            heading: "Un seul statut par invité",
            list: [
              "Invité — l'invitation est partie.",
              "Présent — il a répondu oui.",
              "Absent — il a répondu non.",
              "En attente — pas de réponse, et vous savez depuis combien de temps.",
            ],
            paragraphs: [
              "Tout le reste — choix de menu, allergies, hébergement, transport — est un détail rattaché à l'invité, pas un cinquième statut. C'est cette simplicité qui rend le décompte fiable.",
            ],
          },
          {
            heading: "Définissez tôt l'unité « foyer »",
            paragraphs: [
              "Décidez si vous invitez des personnes ou des foyers, et tenez-vous-y. Un foyer de quatre compté comme une seule ligne, c'est là que les décomptes dérapent. Les enfants comptent comme des convives pour le traiteur même s'ils mangent moins : donnez-leur leur propre ligne avec une note.",
              "Écrivez votre règle sur les accompagnants en une phrase avant d'envoyer les invitations, et appliquez-la à tout le monde. Que la règle soit claire compte plus que la règle elle-même.",
            ],
          },
          {
            heading: "Une seule méthode de RSVP, une seule date limite",
            paragraphs: [
              "Lien unique par invité, formulaire public avec code, ou carton-réponse : choisissez-en une et n'indiquez qu'elle sur l'invitation. Deux méthodes, c'est deux listes à réconcilier.",
              "Fixez la date limite deux à trois semaines avant celle du traiteur, pas le même jour. C'est dans cet écart que se font les relances.",
            ],
          },
          {
            heading: "Relancer sans harceler",
            list: [
              "Un rappel environ deux semaines avant la date limite, à tous ceux qui n'ont pas répondu.",
              "Un message personnel après la date limite, uniquement à ceux dont vous avez vraiment besoin de la réponse.",
              "Ensuite, considérez le silence comme un non et notez-le, pour que le chiffre arrête de bouger.",
            ],
          },
          {
            heading: "Le nombre final",
            paragraphs: [
              "Quand vous donnez un nombre au traiteur, notez quels statuts vous avez comptés et à quelle date. Si le chiffre est remis en question plus tard, vous saurez exactement comment il a été construit — et c'est ce qui fait la différence entre une dernière semaine sereine et une semaine tendue.",
            ],
          },
        ],
        closing:
          "Une liste d'invités ennuyeuse à tenir est une liste qui fonctionne. Un seul endroit, un seul statut par invité, une seule méthode de réponse, une seule date limite.",
      },
    },
  },
  {
    id: "planning-together",
    updated: "2026-09-09",
    content: {
      en: {
        slug: "planning-a-wedding-together",
        title: "Planning a wedding together: dividing the work and deciding well",
        metaTitle: "Planning a wedding together: dividing the work | Nellove",
        description:
          "How partners can share wedding planning fairly: splitting by area instead of by task, deciding who has the final say, handling family expectations, and keeping decisions from being re-opened.",
        readingTime: "6 min read",
        intro:
          "In most couples, one partner ends up holding the entire plan in their head. That is not a fairness problem so much as a structure problem, and it is fixable early with two conversations.",
        sections: [
          {
            heading: "Split by area, not by task",
            paragraphs: [
              "Handing over individual tasks keeps all the thinking with one person — they still have to notice the task, explain it, and check it was done. Handing over an entire area transfers the thinking too.",
              "Areas that divide cleanly: guests and RSVP, food and drink, music and entertainment, photography, outfits, transport and accommodation, budget tracking, ceremony content.",
            ],
          },
          {
            heading: "Agree who has the final say, area by area",
            paragraphs: [
              "Two people with equal say on everything is how a decision gets re-opened four times. For each area, decide who decides — the other partner has input, not a veto.",
              "Keep a very short list of decisions you make together: the budget, the guest list, the venue, and the ceremony. Those genuinely need both of you.",
            ],
          },
          {
            heading: "Write decisions down where both of you can see them",
            list: [
              "What was decided, in one line.",
              "The date it was decided.",
              "The number attached to it, if there is one.",
            ],
            paragraphs: [
              "Most planning arguments are not disagreements — they are two different memories of the same conversation. A shared written record ends them before they start.",
            ],
          },
          {
            heading: "Handle family expectations as a couple",
            paragraphs: [
              "Where family contributes money or has strong expectations, decide together what that contribution buys in terms of influence, and say it out loud early. Each partner then speaks to their own family with the same answer.",
              "A shared line, agreed in advance, protects both of you from being negotiated with separately.",
            ],
          },
          {
            heading: "Protect time that is not about the wedding",
            paragraphs: [
              "Set a weekly slot — thirty minutes is often enough — for planning, and keep it out of the rest of the week. Couples who do this consistently report that the planning gets faster, because decisions accumulate for one conversation instead of interrupting every evening.",
            ],
          },
        ],
        closing:
          "Fair planning is less about equal hours than about equal ownership. Split the areas, name who decides, write it down, and keep the wedding out of the other six days.",
      },
      fr: {
        slug: "organiser-son-mariage-a-deux",
        title: "Organiser son mariage à deux : répartir le travail et bien décider",
        metaTitle: "Organiser son mariage à deux : répartir le travail | Nellove",
        description:
          "Comment partager équitablement l'organisation du mariage : répartir par domaine plutôt que par tâche, décider qui tranche, gérer les attentes des familles et éviter de rouvrir les décisions.",
        readingTime: "6 min de lecture",
        intro:
          "Dans la plupart des couples, une seule personne finit par porter tout le plan dans sa tête. Ce n'est pas tant un problème d'équité qu'un problème de structure, et deux conversations suffisent à le régler tôt.",
        sections: [
          {
            heading: "Répartissez par domaine, pas par tâche",
            paragraphs: [
              "Déléguer des tâches une par une laisse toute la charge mentale à la même personne : elle doit encore y penser, l'expliquer, puis vérifier. Déléguer un domaine entier transfère aussi la réflexion.",
              "Des domaines qui se découpent bien : invités et RSVP, repas et boissons, musique et animation, photo, tenues, transport et hébergement, suivi du budget, contenu de la cérémonie.",
            ],
          },
          {
            heading: "Décidez qui tranche, domaine par domaine",
            paragraphs: [
              "Deux personnes avec voix égale sur tout, c'est ainsi qu'une décision est rouverte quatre fois. Pour chaque domaine, désignez qui décide : l'autre donne son avis, pas un veto.",
              "Gardez une très courte liste de décisions prises ensemble : le budget, la liste d'invités, le lieu et la cérémonie. Celles-là méritent vraiment d'être à deux.",
            ],
          },
          {
            heading: "Écrivez les décisions là où vous les voyez tous les deux",
            list: [
              "Ce qui a été décidé, en une ligne.",
              "La date de la décision.",
              "Le montant associé, s'il y en a un.",
            ],
            paragraphs: [
              "La plupart des tensions ne sont pas des désaccords : ce sont deux souvenirs différents de la même conversation. Une trace écrite partagée les évite avant qu'elles ne commencent.",
            ],
          },
          {
            heading: "Gérez les attentes des familles en couple",
            paragraphs: [
              "Quand une famille contribue financièrement ou a des attentes fortes, décidez ensemble ce que cette contribution donne comme influence, et dites-le tôt. Chacun parle ensuite à sa propre famille avec la même réponse.",
              "Une position commune, décidée à l'avance, vous évite d'être négociés séparément.",
            ],
          },
          {
            heading: "Protégez le temps qui n'est pas consacré au mariage",
            paragraphs: [
              "Réservez un créneau hebdomadaire — trente minutes suffisent souvent — pour l'organisation, et sortez-la du reste de la semaine. Les couples qui s'y tiennent avancent plus vite : les décisions s'accumulent pour une seule conversation au lieu d'interrompre chaque soirée.",
            ],
          },
        ],
        closing:
          "Une organisation équitable tient moins aux heures qu'à la responsabilité. Répartissez les domaines, nommez qui tranche, écrivez-le, et laissez le mariage en dehors des six autres jours.",
      },
    },
  },
  {
    id: "how-many-guests",
    updated: "2026-09-10",
    content: {
      en: {
        slug: "how-many-guests-to-invite",
        title: "How many guests should you invite?",
        metaTitle: "How many wedding guests should you invite? A method | Nellove",
        description:
          "A practical method for setting a wedding guest count: work from what the day costs per person, use the three-list rule, and handle plus-ones and children before invitations go out.",
        readingTime: "6 min read",
        intro:
          "Guest count is the single number that moves your wedding budget the most, and it is usually decided last. Here is how to settle it early, without turning it into a family negotiation.",
        sections: [
          {
            heading: "Decide the number before the names",
            paragraphs: [
              "Start from what you can spend, not from who you might invite. Catering, rentals, stationery and often the venue are priced per head, so the guest count effectively sets your budget rather than fitting inside it.",
              "Agree an upper limit as a couple first. Every later conversation becomes easier once there is a number to point at.",
            ],
          },
          {
            heading: "The three-list method",
            list: [
              "List A: people whose absence would change the day for you.",
              "List B: people you would love to have if the room allows.",
              "List C: people invited out of habit or obligation.",
            ],
            paragraphs: [
              "Invite all of list A, as much of list B as your limit allows, and treat list C as a conversation rather than an assumption. Doing this on paper is far less painful than doing it after invitations are printed.",
            ],
          },
          {
            heading: "Plus-ones and children: decide the rule, not the exceptions",
            paragraphs: [
              "Write one rule and apply it evenly — for example, partners of guests are invited when the relationship is established, or children are welcome at the ceremony only. A rule applied to everyone is defensible; a list of exceptions is not.",
              "Put the rule on the invitation itself. Guests who read it rarely ask.",
            ],
          },
          {
            heading: "Plan for the replies you will not get",
            paragraphs: [
              "Some invitations decline and some never answer. Rather than guessing a percentage that may not match your circle, set a clear RSVP deadline, then chase the missing replies directly by phone or message about a week after it passes.",
              "Confirm your caterer's final head-count date at booking, and set your RSVP deadline at least a week before it.",
            ],
          },
        ],
        closing:
          "Set an upper limit, sort names into three lists, write one rule for plus-ones and children, and chase the stragglers before the caterer needs the number. That is the whole method.",
      },
      fr: {
        slug: "combien-d-invites-au-mariage",
        title: "Combien d'invités inviter à son mariage ?",
        metaTitle: "Combien d'invités pour un mariage ? Une méthode simple | Nellove",
        description:
          "Une méthode concrète pour fixer le nombre d'invités : partir du coût par personne, utiliser la règle des trois listes, et trancher les accompagnants et les enfants avant l'envoi des invitations.",
        readingTime: "6 min de lecture",
        intro:
          "Le nombre d'invités est le chiffre qui pèse le plus sur le budget, et c'est presque toujours le dernier fixé. Voici comment le décider tôt, sans en faire une négociation familiale.",
        sections: [
          {
            heading: "Fixez le nombre avant les noms",
            paragraphs: [
              "Partez de ce que vous pouvez dépenser, pas de qui vous pourriez inviter. Le traiteur, la location, la papeterie et souvent le lieu se facturent par personne : le nombre d'invités fixe le budget plus qu'il n'y entre.",
              "Mettez-vous d'accord à deux sur un plafond. Toutes les discussions suivantes deviennent plus simples quand il existe un chiffre à montrer.",
            ],
          },
          {
            heading: "La méthode des trois listes",
            list: [
              "Liste A : les personnes dont l'absence changerait la journée pour vous.",
              "Liste B : celles que vous aimeriez avoir si la place le permet.",
              "Liste C : celles invitées par habitude ou par obligation.",
            ],
            paragraphs: [
              "Invitez toute la liste A, autant de la liste B que le plafond l'autorise, et traitez la liste C comme une discussion plutôt qu'une évidence. Le faire sur papier fait bien moins mal qu'après l'impression des invitations.",
            ],
          },
          {
            heading: "Accompagnants et enfants : décidez la règle, pas les exceptions",
            paragraphs: [
              "Écrivez une règle et appliquez-la à tout le monde — par exemple, les conjoints sont invités quand la relation est installée, ou les enfants sont bienvenus à la cérémonie uniquement. Une règle générale se défend ; une liste d'exceptions, non.",
              "Indiquez la règle sur l'invitation. Les invités qui la lisent posent rarement la question.",
            ],
          },
          {
            heading: "Anticipez les réponses qui n'arriveront pas",
            paragraphs: [
              "Certains déclinent, d'autres ne répondent jamais. Plutôt que de deviner un pourcentage qui ne correspond peut-être pas à votre entourage, fixez une date limite claire, puis relancez directement les manquants une semaine après.",
              "Demandez au traiteur, dès la réservation, la date à laquelle il lui faut le nombre définitif, et placez votre date limite RSVP au moins une semaine avant.",
            ],
          },
        ],
        closing:
          "Un plafond, trois listes, une règle unique pour les accompagnants et les enfants, et une relance avant l'échéance du traiteur : c'est toute la méthode.",
      },
    },
  },
  {
    id: "rsvp-wording",
    updated: "2026-09-10",
    content: {
      en: {
        slug: "wedding-rsvp-wording",
        title: "RSVP wording that actually gets replies",
        metaTitle: "Wedding RSVP wording — examples that get replies | Nellove",
        description:
          "How to word a wedding RSVP so guests answer: one method, one deadline, the questions you actually need, and example wording for cards, links and public forms.",
        readingTime: "5 min read",
        intro:
          "Most missing RSVPs are not rudeness. They are unclear instructions: two ways to reply, no deadline, or a question the guest cannot answer yet. Fix the wording and the replies arrive.",
        sections: [
          {
            heading: "One method, one deadline",
            paragraphs: [
              "Offer a single way to reply. A card and a link and a text message means three places to reconcile and three chances for the same guest to answer twice.",
              "Give a date, not a season. \u201cPlease reply by 12 June\u201d outperforms \u201cplease reply soon\u201d by a wide margin.",
            ],
          },
          {
            heading: "Ask only what you will use",
            list: [
              "Are you coming — yes or no.",
              "Who exactly is coming, named.",
              "Anything you must tell the caterer, such as allergies or a dietary requirement.",
              "Only if it applies: which parts of the day they will attend.",
            ],
            paragraphs: [
              "Every extra question lowers the reply rate. Song requests and travel plans can be asked later, to the people who said yes.",
            ],
          },
          {
            heading: "Example wording",
            list: [
              "Card: \u201cKindly reply by 12 June. M. ____ will / will not attend. Dietary requirements: ____\u201d",
              "Link: \u201cPlease reply at [link] by 12 June. The link is personal to you and takes a minute.\u201d",
              "Public form with a passcode: \u201cReply at [link] by 12 June using the code on this card.\u201d",
              "Reminder message: \u201cWe are finalising numbers with the caterer this week. If you have not replied yet, could you let us know either way by Friday?\u201d",
            ],
          },
          {
            heading: "Make declining easy",
            paragraphs: [
              "A guest who cannot come often delays replying because saying no feels awkward. Wording that names the option — will attend, will not attend — gets you an answer faster than wording that only imagines a yes.",
            ],
          },
        ],
        closing:
          "One reply method, one dated deadline, the three questions you actually need, and a plain reminder a week later. That is what turns an invitation list into a head count.",
      },
      fr: {
        slug: "formulation-rsvp-mariage",
        title: "Formuler un RSVP de mariage pour obtenir des réponses",
        metaTitle: "RSVP mariage : quelle formulation pour avoir des réponses | Nellove",
        description:
          "Comment formuler un RSVP de mariage pour que les invités répondent : un seul moyen, une date limite, les bonnes questions, et des exemples pour carton, lien ou formulaire public.",
        readingTime: "5 min de lecture",
        intro:
          "La plupart des RSVP manquants ne sont pas de l'impolitesse, mais des consignes floues : deux façons de répondre, aucune date, ou une question à laquelle l'invité ne peut pas encore répondre. Changez la formulation et les réponses arrivent.",
        sections: [
          {
            heading: "Un seul moyen, une seule date",
            paragraphs: [
              "Proposez une seule façon de répondre. Un carton, un lien et un message, c'est trois endroits à recouper et trois occasions qu'un même invité réponde deux fois.",
              "Donnez une date précise, pas une saison. « Merci de répondre avant le 12 juin » fonctionne bien mieux que « merci de répondre rapidement ».",
            ],
          },
          {
            heading: "Ne demandez que ce qui vous servira",
            list: [
              "Venez-vous — oui ou non.",
              "Qui vient exactement, nommément.",
              "Ce que le traiteur doit savoir : allergies, régime particulier.",
              "Seulement si c'est pertinent : à quelles parties de la journée ils assistent.",
            ],
            paragraphs: [
              "Chaque question supplémentaire fait baisser le taux de réponse. Les chansons souhaitées et les trajets se demandent plus tard, à ceux qui ont dit oui.",
            ],
          },
          {
            heading: "Exemples de formulation",
            list: [
              "Carton : « Merci de répondre avant le 12 juin. M. ____ sera présent·e / ne pourra pas venir. Régime particulier : ____ »",
              "Lien : « Merci de répondre sur [lien] avant le 12 juin. Ce lien vous est personnel et prend une minute. »",
              "Formulaire public avec code : « Répondez sur [lien] avant le 12 juin avec le code indiqué sur ce carton. »",
              "Relance : « Nous finalisons les nombres avec le traiteur cette semaine. Si vous n'avez pas encore répondu, pouvez-vous nous dire oui ou non d'ici vendredi ? »",
            ],
          },
          {
            heading: "Rendez le refus facile",
            paragraphs: [
              "Un invité qui ne peut pas venir repousse souvent sa réponse parce que dire non est inconfortable. Une formulation qui nomme les deux options — sera présent·e, ne pourra pas venir — obtient une réponse plus vite qu'une formulation qui n'envisage que le oui.",
            ],
          },
        ],
        closing:
          "Un seul moyen de répondre, une date précise, trois questions utiles et une relance simple une semaine après : c'est ce qui transforme une liste d'invitations en nombre de couverts.",
      },
    },
  },
  {
    id: "wedding-budget",
    updated: "2026-09-10",
    content: {
      en: {
        slug: "how-to-build-a-wedding-budget",
        title: "How to build a wedding budget you can actually use",
        metaTitle: "How to build a wedding budget — a practical method | Nellove",
        description:
          "Build a wedding budget that survives contact with reality: start from a total, track estimated, quoted, paid and remaining due, and plan the payment dates as carefully as the amounts.",
        readingTime: "7 min read",
        intro:
          "A wedding budget fails for one of two reasons: it is a list of guesses nobody updates, or it tracks totals but not when the money is due. Here is how to build one that keeps working.",
        sections: [
          {
            heading: "Start from the total you have, not the wedding you saw",
            paragraphs: [
              "Agree three things before allocating anything: the total you are prepared to spend, who is contributing and how much, and whether any contribution comes with expectations attached.",
              "We deliberately do not publish average cost percentages. Prices differ enormously by country, city and season, and a national average is a poor guide for your venue in your month.",
            ],
          },
          {
            heading: "Four numbers per line, not one",
            list: [
              "Estimated: what you think it will cost before you have asked.",
              "Quoted: the number a provider has actually given you in writing.",
              "Paid: what has left your account, including deposits.",
              "Remaining due: what is still owed, and by when.",
            ],
            paragraphs: [
              "A budget with only estimates tells you what you hoped. These four columns tell you where you stand, which is the question you will ask every month from the halfway point onwards.",
            ],
          },
          {
            heading: "Get quotes before you spread the money",
            paragraphs: [
              "Book and quote the largest items first — usually the venue, catering and photography. Once those are real numbers rather than estimates, whatever is left is what you truly have for everything else.",
              "Ask each provider what is not included. The gap between a quote and a final invoice is usually service charges, overtime, delivery or taxes.",
            ],
          },
          {
            heading: "Track the dates, not just the amounts",
            paragraphs: [
              "Deposits, second instalments and final balances rarely fall conveniently. Write the due date next to every unpaid amount and check the list monthly, so nothing lands in the same week as the wedding.",
            ],
          },
          {
            heading: "Keep a real contingency",
            paragraphs: [
              "Hold back an untouched reserve for the things nobody plans: alterations, extra hours, a supplier's travel, a last-minute rental. If you never need it, it is money you did not spend.",
            ],
          },
        ],
        closing:
          "One agreed total, four numbers on every line, real quotes before allocation, due dates written down, and a reserve you do not touch. A budget built that way still tells the truth in month nine.",
      },
      fr: {
        slug: "budget-mariage-comment-le-construire",
        title: "Budget de mariage : comment en construire un vraiment utile",
        metaTitle: "Budget mariage : une méthode concrète pour le construire | Nellove",
        description:
          "Construire un budget de mariage qui résiste à la réalité : partir d'un total, suivre l'estimé, le soumissionné, le payé et le reste dû, et planifier les dates de paiement autant que les montants.",
        readingTime: "7 min de lecture",
        intro:
          "Un budget de mariage échoue pour deux raisons : c'est une liste d'estimations que personne ne met à jour, ou il suit les totaux sans suivre les échéances. Voici comment en construire un qui continue de servir.",
        sections: [
          {
            heading: "Partez du total dont vous disposez",
            paragraphs: [
              "Mettez-vous d'accord sur trois points avant toute répartition : le total que vous êtes prêts à dépenser, qui contribue et à quelle hauteur, et si une contribution s'accompagne d'attentes.",
              "Nous ne publions volontairement pas de pourcentages moyens. Les prix varient énormément selon le pays, la ville et la saison : une moyenne nationale est un mauvais guide pour votre lieu, votre mois.",
            ],
          },
          {
            heading: "Quatre chiffres par ligne, pas un seul",
            list: [
              "Estimé : ce que vous pensez que ça coûtera avant d'avoir demandé.",
              "Soumissionné : le montant qu'un prestataire vous a donné par écrit.",
              "Payé : ce qui est sorti du compte, acomptes compris.",
              "Reste dû : ce qui est encore à payer, et pour quand.",
            ],
            paragraphs: [
              "Un budget qui ne contient que des estimations raconte ce que vous espériez. Ces quatre colonnes disent où vous en êtes — la question que vous poserez chaque mois à partir de la mi-parcours.",
            ],
          },
          {
            heading: "Obtenez des devis avant de répartir",
            paragraphs: [
              "Réservez et faites chiffrer les plus gros postes d'abord — en général le lieu, le traiteur et la photographie. Une fois ces montants réels, ce qui reste est ce dont vous disposez vraiment pour le reste.",
              "Demandez à chaque prestataire ce qui n'est pas inclus. L'écart entre un devis et la facture finale vient presque toujours des frais de service, des heures supplémentaires, de la livraison ou des taxes.",
            ],
          },
          {
            heading: "Suivez les dates, pas seulement les montants",
            paragraphs: [
              "Acomptes, deuxièmes versements et soldes tombent rarement au bon moment. Notez l'échéance à côté de chaque montant impayé et relisez la liste chaque mois, pour qu'aucun paiement n'atterrisse la semaine du mariage.",
            ],
          },
          {
            heading: "Gardez une vraie réserve",
            paragraphs: [
              "Mettez de côté une réserve intouchée pour ce que personne ne prévoit : retouches, heures supplémentaires, déplacement d'un prestataire, location de dernière minute. Si elle ne sert pas, c'est de l'argent non dépensé.",
            ],
          },
        ],
        closing:
          "Un total assumé, quatre chiffres par ligne, des devis avant la répartition, des échéances écrites et une réserve intouchée : un budget construit ainsi dit encore la vérité au neuvième mois.",
      },
    },
  },
  {
    id: "wedding-planning-timeline",
    updated: "2026-09-30",
    content: {
      en: {
        slug: "wedding-planning-timeline",
        title: "How to plan a wedding timeline: 12, 9, 6 or 3 months",
        metaTitle: "How to plan a wedding timeline — 12, 9, 6 or 3 months | Nellove",
        description:
          "Build a wedding planning timeline from your actual date: what fits in 12, 9, 6 or 3 months, the fixed milestones that never move, and how to write the day-of timeline.",
        readingTime: "7 min read",
        intro:
          "A wedding timeline is not one standard list. It is your date minus a few fixed lead times. Start from the date, work backwards from the milestones that cannot move, and the rest of the plan falls into place, whether you have a year or a season.",
        sections: [
          {
            heading: "Step 1: find the milestones that cannot move",
            list: [
              "The legal lead time where you marry: notice periods, licences or publication of banns. Check the official source for your country or province first.",
              "The caterer's final head-count date.",
              "The RSVP deadline, set at least a week before that head-count date.",
              "The invitation send date, about six to eight weeks before the wedding.",
              "Outfit delivery, leaving several weeks for alterations.",
            ],
            paragraphs: [
              "Put these on a calendar first. Everything else fits in the space between them.",
            ],
          },
          {
            heading: "12 months or more: the relaxed timeline",
            list: [
              "Months 12–10: budget, guest count, date, venue.",
              "Months 10–8: photography, catering, music, officiant.",
              "Months 8–6: save-the-dates, guest list with addresses, outfits ordered.",
              "Months 6–3: florist, cake, hair and makeup, legal paperwork, invitations ordered.",
              "Months 2–0: invitations out, RSVPs chased, final numbers, day-of timeline shared.",
            ],
          },
          {
            heading: "9 months: the common timeline",
            paragraphs: [
              "Compress the first two phases into the first two months: book the venue and the one-wedding-a-day providers (photographer, caterer, musicians) together. From month six onwards, the timeline is the same as a twelve-month plan.",
            ],
          },
          {
            heading: "6 months: decide faster, book availability",
            list: [
              "Week 1–2: budget, guest count and the date the venue can offer.",
              "Month 1: every provider booked. Choose on availability, not on the perfect option.",
              "Month 2: save-the-dates or go straight to invitations.",
              "Month 3–4: outfits, legal paperwork, remaining details.",
              "Final 6–8 weeks: same as any timeline.",
            ],
          },
          {
            heading: "3 months or less: the short timeline",
            paragraphs: [
              "Start with the legal lead time, since it may be most of your runway. Then book the venue and caterer in the same week, send invitations immediately with a short RSVP window, and drop anything that needs a long lead time, such as custom stationery or made-to-order outfits. A short timeline works when fewer decisions are made, not when the same decisions are made faster.",
            ],
          },
          {
            heading: "The day-of timeline",
            paragraphs: [
              "Write it from the ceremony time outwards. Work backwards for preparation, hair and makeup and travel. Work forwards for photos, arrivals, the meal, speeches and music. Add a named contact and an arrival time for every provider, then share it a week or two before the day.",
            ],
          },
        ],
        closing:
          "Fixed milestones first, bookings next, details last. Nellove's free checklist builds this timeline from your real wedding date, and Nellove itself keeps it up to date. It is free forever.",
      },
      fr: {
        slug: "retroplanning-mariage",
        title: "Rétroplanning de mariage : 12, 9, 6 ou 3 mois",
        metaTitle: "Rétroplanning mariage — en 12, 9, 6 ou 3 mois | Nellove",
        description:
          "Construire le rétroplanning de votre mariage à partir de la vraie date : ce qui tient en 12, 9, 6 ou 3 mois, les jalons qui ne bougent pas, et le déroulé du jour J.",
        readingTime: "7 min de lecture",
        intro:
          "Un rétroplanning n'est pas une liste standard. C'est votre date moins quelques délais incompressibles. Partez de la date, remontez depuis les jalons qui ne bougent pas, et le reste se place tout seul, que vous ayez un an ou une saison.",
        sections: [
          {
            heading: "Étape 1 : repérer les jalons incompressibles",
            list: [
              "Le délai légal du lieu du mariage : publication des bans, avis ou licence. Vérifiez d'abord la source officielle de votre pays ou province.",
              "La date du nombre définitif demandé par le traiteur.",
              "La date limite de réponse, au moins une semaine avant.",
              "L'envoi des invitations, environ six à huit semaines avant le mariage.",
              "La livraison des tenues, en gardant plusieurs semaines pour les retouches.",
            ],
            paragraphs: ["Placez-les d'abord dans le calendrier. Tout le reste s'organise entre eux."],
          },
          {
            heading: "12 mois ou plus : le rétroplanning confortable",
            list: [
              "Mois 12–10 : budget, nombre d'invités, date, lieu.",
              "Mois 10–8 : photographe, traiteur, musique, célébrant.",
              "Mois 8–6 : save the date, liste d'invités avec adresses, tenues commandées.",
              "Mois 6–3 : fleuriste, gâteau, coiffure et maquillage, démarches, invitations commandées.",
              "Mois 2–0 : invitations envoyées, relances, nombre final, déroulé du jour J partagé.",
            ],
          },
          {
            heading: "9 mois : le rétroplanning courant",
            paragraphs: [
              "Resserrez les deux premières phases sur les deux premiers mois : réservez ensemble le lieu et les prestataires qui ne font qu'un mariage par jour (photographe, traiteur, musiciens). À partir de six mois, c'est le même rétroplanning qu'en douze mois.",
            ],
          },
          {
            heading: "6 mois : décider vite, réserver la disponibilité",
            list: [
              "Semaines 1–2 : budget, nombre d'invités et date disponible au lieu.",
              "Mois 1 : tous les prestataires réservés. Choisissez sur la disponibilité, pas sur l'option parfaite.",
              "Mois 2 : save the date ou invitations directement.",
              "Mois 3–4 : tenues, démarches, derniers détails.",
              "6–8 dernières semaines : comme tout rétroplanning.",
            ],
          },
          {
            heading: "3 mois ou moins : le rétroplanning court",
            paragraphs: [
              "Commencez par le délai légal, qui peut occuper l'essentiel du temps disponible. Réservez ensuite le lieu et le traiteur la même semaine, envoyez les invitations tout de suite avec un délai de réponse court, et renoncez à ce qui demande un long délai, comme la papeterie sur mesure ou les tenues faites sur commande. Un délai court fonctionne en prenant moins de décisions, pas en prenant les mêmes plus vite.",
            ],
          },
          {
            heading: "Le déroulé du jour J",
            paragraphs: [
              "Écrivez-le à partir de l'heure de la cérémonie. Remontez le temps pour les préparatifs, la coiffure, le maquillage et les trajets. Avancez pour les photos, l'arrivée des invités, le repas, les discours et la musique. Ajoutez un contact et une heure d'arrivée pour chaque prestataire, puis partagez-le une à deux semaines avant.",
            ],
          },
        ],
        closing:
          "Les jalons fixes d'abord, les réservations ensuite, les détails en dernier. La checklist gratuite de Nellove calcule ce rétroplanning à partir de votre vraie date, et Nellove le tient à jour. C'est gratuit pour toujours.",
      },
    },
  },
];

export const getGuideBySlug = (locale: Locale, slug: string) =>
  GUIDES.find((g) => g.content[locale].slug === slug);

export const findGuideByAnySlug = (slug: string) =>
  GUIDES.find((g) => g.content.en.slug === slug || g.content.fr.slug === slug);
